/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { forkJoin, merge, Observable } from 'rxjs';
import { catchError, concatMap, filter, first, map, mergeMap, take, tap } from 'rxjs/operators';
import * as xlsx from 'xlsx';

import { MapConfigService } from '../../map/services/mapConfig.service';
import { RedlineConfigService } from '../../../components/redline/services/redline-config.service';
import { LoggerService } from '../../shared/services/logger.service';
import { MapService } from '../../map/services/map.service';
import { QueryService } from '../../map/services/query.service';
import { RestService } from '../../shared/services/rest.service';
import { DownloadService } from '../../shared/services/download.service';
import { Store } from '@ngxs/store';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { RedlineState } from '../../../components/redline/state/redline.state';
import { SearchDefinition, SearchField, SearchFormModel, SearchListEntry, SearchResults } from '../state/search.model';
import { ClearSearchResultFeatures, DisplaySearchResultFeatures } from '../state/search.actions';
import { DisplayFeaturePayload } from '../../map/state/active-map.model';
import { RedlineLayerConfiguration } from '../../../components/redline/state/redline.model';
import { MsLayerNode } from '../../map/state/map-config.model';
import { FeatureService } from '../../map/services/feature.service';

@Injectable()
export class SearchService {
    searchParams: any;

    private _searchName: string;
    private needsFeatures: boolean;
    private _sortColumn: string;
    private _searchResults: SearchResults;
    private _definitionCache: {};

    /**
     * @param logger
     * @param restService
     * @param mapService {MapService}
     * @param queryService
     * @param featureService {FeatureService}
     * @param downloadService {DownloadService}
     * @param mapConfig {MapConfigService}
     * @param redlineConfig {RedlineConfigService}
     * @param store {Store}
     */
    constructor(
        private logger: LoggerService,
        private restService: RestService,
        private mapService: MapService,
        private queryService: QueryService,
        private featureService: FeatureService,
        private downloadService: DownloadService,
        private mapConfig: MapConfigService,
        private redlineConfig: RedlineConfigService,
        private store: Store
    ) {
        this._searchResults = null;

        this._sortColumn = '';

        // cache definitions
        this._definitionCache = {};
    }

    getSearches(): Observable<SearchListEntry[]> {
        return forkJoin([
            this.mapConfig.getAllLayers$().pipe(take(1)),
            this.store.select(RedlineState.viewable).pipe(take(1))
        ]).pipe(
            // filter empty entries and merge the arrays
            map(([ layers, redlineLayers ]) =>
                [].concat.apply([], [ layers.filter((b) => b), redlineLayers.filter((b) => b) ])
            ),
            map((layers: (MsLayerNode | RedlineLayerConfiguration)[]) => layers.map((l) => l.name)),
            mergeMap((searchLayers) => {
                const mapLayers = {};
                mapLayers[this.mapConfig.mapName] = searchLayers;
                return this.restService.apiPost<SearchListEntry[]>('search/list', { layers: mapLayers });
            })
        );
    }

    getSearchDefinitions() {
        return this.restService.apiGet<SearchDefinition[]>('search/definitions');
    }

    getSearchDefinition(searchName) {
        return this.restService.apiGet<SearchDefinition>(`search/definition/${ searchName }`);
    }

    runSearch(
        searchName: string,
        searchDefinition: SearchDefinition,
        searchParams?,
        page?: number,
        limit?: number,
        sort?: string,
        sortDirection?: string
    ) {
        this.store.dispatch(new ClearSearchResultFeatures({displayLayer: 'searchFeatures'}));
        const params = {
            fields: this.transformCriteria(searchParams),
            paging: { limit, page, sort, sortDirection }
        };
        return this.restService.apiPost<SearchResults>(`search/run/${ searchName }`, params).pipe(
            map((searchResults: SearchResults) => this.processResults(searchResults)),
            tap((searchResults: SearchResults) => {
                if (searchResults.wantsfeatures && searchResults.count > 0) {
                    this.getResultFeatures(searchName, params).subscribe((featureResults) => {
                        this.displayFeatures(searchDefinition, featureResults);
                    });
                }
            })
        );
    }

    runSearchForPrint(
        searchName: string,
        searchDefinition: SearchDefinition,
        searchParams?,
        sort?: string,
        sortDirection?: string
    ) {
        const params = {
            fields: this.transformCriteria(searchParams),
            paging: { sort, sortDirection }
        };
        return this.restService
            .apiPost<SearchResults>(`search/run/${ searchName }`, params)
            .pipe(map((searchResults: SearchResults) => this.processResults(searchResults)));
    }

    clearResults() {
        this._searchResults = null;
        this.needsFeatures = true;
        this.clearSearchFeatures();
    }

    clearSearchParams() {
        this.searchParams = {};
    }

    restoreSearch() {}

    prepareCSVData(searchResults: SearchResults): Blob {
        // transform data to [0] header row, [...] data
        const csvData = [];
        const fields = { ...searchResults.resultFields };
        csvData.push(fields.map((item) => item.displayName || item.name));
        searchResults.data.forEach((row) => {
            const csvRow = [];
            fields.forEach((field) => {
                if (field.type === 'date') {
                    csvRow.push(row[field.name].toLocaleDateString());
                } else {
                    csvRow.push(row[field.name]);
                }
            });
            csvData.push(csvRow);
        });
        if (searchResults.totals) {
            const totalsRow = [ 'Totals' ];
            searchResults.totals.forEach((totalsField) => {
                fields.forEach((field, index) => {
                    // skip first column
                    if (index === 0) {
                        return;
                    }
                    if (field.name === totalsField.name) {
                        totalsRow.push(totalsField.value);
                    } else {
                        totalsRow.push('');
                    }
                });
            });
            csvData.push(totalsRow);
        }
        if (searchResults.averages) {
            const averagesRow = [ 'Averages' ];
            searchResults.averages.forEach((averagesField) => {
                fields.forEach((field, index) => {
                    // skip first column
                    if (index === 0) {
                        return;
                    }
                    if (field.name === averagesField.name) {
                        averagesRow.push(averagesField.value);
                    } else {
                        averagesRow.push('');
                    }
                });
            });
            csvData.push(averagesRow);
        }
        return this.downloadService.createCSVBlob(csvData);
    }

    /*
     downloadCSV(csvData) {
     // const csvBlob = new Blob([csvData], {type: "text/csv"});
     const dateString = moment().format("YYYY-MM-DD_HHmmss");
     this.downloadService.downloadBlob(csvData, `${this.searchName}_${dateString}.csv`);
     }
     */

    downloadExcel(excelData) {
        const dateString = moment().format('YYYY-MM-DD_HHmmss');
        // TODO: include search name
        this.downloadService.downloadBlob(excelData, `search_result_${ dateString }.xlsx`);
    }

    prepareExcelData(searchResults: SearchResults) {
        // transform data to Common Spreadsheet Format used by js-xlsx
        const sheet = [];

        // column headers
        const fields = [ ...searchResults.resultFields ];
        sheet.push(fields.map((item) => item.displayName || item.name));
        searchResults.data.forEach((row) => {
            const csvRow = [];
            fields.forEach((field) => {
                if (field.type === 'date') {
                    csvRow.push(row[field.name].toLocaleDateString());
                } else {
                    csvRow.push(row[field.name]);
                }
            });
            sheet.push(csvRow);
        });
        if (searchResults.totals) {
            const totalsRow = [ 'Totals' ];
            searchResults.totals.forEach((totalsField) => {
                fields.forEach((field, index) => {
                    // skip first column
                    if (index === 0) {
                        return;
                    }
                    if (field.name === totalsField.name) {
                        totalsRow.push(totalsField.value);
                    } else {
                        totalsRow.push('');
                    }
                });
            });
            sheet.push(totalsRow);
        }
        if (searchResults.averages) {
            const averagesRow = [ 'Averages' ];
            searchResults.averages.forEach((averagesField) => {
                fields.forEach((field, index) => {
                    // skip first column
                    if (index === 0) {
                        return;
                    }
                    if (field.name === averagesField.name) {
                        averagesRow.push(averagesField.value);
                    } else {
                        averagesRow.push('');
                    }
                });
            });
            sheet.push(averagesRow);
        }
        const worksheet = xlsx.utils.aoa_to_sheet(sheet);
        const wb: xlsx.WorkBook = { SheetNames: [], Sheets: {} };
        const sheetName = 'SearchResults';
        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = worksheet;
        const out = xlsx.write(wb, { bookType: 'xlsx', bookSST: false, type: 'binary' });
        return this.downloadService.createExcelBlob(out);
    }

    /**
     * Get full search data for csv
     */
    runFullSearch(searchName?, searchParams?, sort?) {
        if (searchName) {
            this._searchName = searchName;
        } else if (this._searchName) {
            searchName = this._searchName;
        } else {
            return null;
        }
        if (searchParams) {
            this.searchParams = searchParams;
        } else if (this.searchParams) {
            searchParams = this.searchParams;
        } else {
            return null;
        }

        const params = {
            fields: this.transformCriteria(searchParams),
            paging: {
                sort
            }
        };
        return this.restService
            .apiPost(`search/run/${ searchName }`, params)
            .pipe(map((searchResults) => this.processResults(searchResults)));
    }

    private displayFeatures(searchDefinition, featureResults) {
        // TODO: Refactor this to make use of QueryService.queryLayers$
        // const queryLayer = this.mapService.getVectorLayer('searchFeatures');
        // const querySource = queryLayer.getSource();
        const rows = featureResults.data;
        // querySource.clear();

        /*
         * When the layer definition has a layer field defined, look for
         * the field value DB_LAYER_ID.
         */
        if (searchDefinition.layerfield === '') {
            const values = [];
            for (const row of rows) {
                values.push(row.DB_FEATURE_ID);
            }
            const propertyFilter = {
                filter: {
                    property: searchDefinition.objectkey,
                    values
                },
                type: 'property'
            };

            const redlineFeatures$ = this.redlineConfig.getLayer$(searchDefinition.layer).pipe(
                filter((redlineLayer) => !!redlineLayer),
                concatMap(() => this.queryService.getRedlineFeatureData$(searchDefinition.layer, propertyFilter)),
                tap((features) => {
                    let searchResultStyles = [];
                    if (features.length > 0) {
                        searchResultStyles = this.mapConfig.getStyles(
                            'searchFeatures',
                            this.mapService.getGeometryCategoryByFeature(features[0]),
                            this.store.selectSnapshot(AppConfigState.searches).styles
                        );
                        const featureIds = this.featureService.storeFeatures(features);
                        this.featureService.updateFeatureStyle(featureIds, searchResultStyles);
                        const payload: DisplayFeaturePayload = {
                            displayLayer: 'searchFeatures',
                            featureIds
                        };
                        this.store.dispatch(new DisplaySearchResultFeatures(payload));
                    }
                })
            );

            const wmsFeatures$ = this.redlineConfig.getLayer$(searchDefinition.layer).pipe(
                filter((redlineLayer) => !redlineLayer),
                concatMap(() => this.queryService.getWmsFeatureData$([ searchDefinition.layer ], propertyFilter)),
                tap((features) => {
                    if (features.length > 0) {
                        const queryStyles = this.mapConfig.getStyles(
                            'searchFeatures',
                            this.mapService.getGeometryCategoryByFeature(features[0]),
                            this.store.selectSnapshot(AppConfigState.searches).styles
                        );
                        const featureIds = this.featureService.storeFeatures(features);
                        this.featureService.updateFeatureStyle(featureIds, queryStyles);
                        const payload: DisplayFeaturePayload = {
                            displayLayer: 'searchFeatures',
                            featureIds
                        };
                        this.store.dispatch(new DisplaySearchResultFeatures(payload));
                    }
                }),
                catchError((err, caught$) => {
                    this.logger.error(err);
                    return caught$;
                })
            );

            merge(redlineFeatures$, wmsFeatures$).subscribe();
        } else {
            let layerFilter;
            const layers = new Map();
            for (const row of rows) {
                const layerName = row.DB_LAYER_ID;
                if (layers.has(layerName)) {
                    layerFilter = layers.get(layerName);
                } else {
                    layerFilter = {
                        filter: {
                            filter: {
                                property: searchDefinition.objectkey,
                                values: []
                            },
                            type: 'property'
                        },
                        layerName
                    };
                }
                layerFilter.filter.filter.values.push(row.DB_FEATURE_ID);
                layers.set(layerName, layerFilter);
            }
            for (const [ layer, redlineLayerFilter ] of layers) {
                const layerIsRedline$ = this.redlineConfig.getLayer$(layer).pipe(
                    filter((layerConfig) => !!layerConfig),
                    concatMap(() => this.queryService.getRedlineFeatureData$(layer, redlineLayerFilter))
                );

                const layerIsNotRedline$ = this.redlineConfig.getLayer$(layer).pipe(
                    filter((layerConfig) => !layerConfig),
                    concatMap(() => this.queryService.getWmsFeatureData$([ layer ], redlineLayerFilter.filter))
                );

                merge(layerIsRedline$, layerIsNotRedline$).subscribe(
                    (features) => {
                        if (features.length > 0) {
                            const queryStyles = this.mapConfig.getStyles(
                                'searchFeatures',
                                this.mapService.getGeometryCategoryByFeature(features[0]),
                                this.store.selectSnapshot(AppConfigState.searches).styles
                            );

                            const featureIds = this.featureService.storeFeatures(features);
                            this.featureService.updateFeatureStyle(featureIds, queryStyles);
                            const payload: DisplayFeaturePayload = {
                                displayLayer: 'searchFeatures',
                                featureIds
                            };
                            this.store.dispatch(new DisplaySearchResultFeatures(payload));
                        }
                    },
                    (reason) => {
                        this.logger.error(reason);
                    }
                );
            }
        }
    }

    private getResultFeatures(searchName, searchParams) {
        return this.restService.apiPost(`search/features/${ searchName }`, searchParams);
    }

    private clearSearchFeatures() {
        const queryLayer = this.mapService.getVectorLayer('searchFeatures', null, false);
        const querySource = queryLayer.getSource();
        querySource.clear();
    }

    /**
     * Transform param object to an array containing only param items with search values
     * @returns {Array}
     * @param criteria {SearchFormModel}
     */
    private transformCriteria(criteria: SearchFormModel) {
        const filteredParams = [];
        criteria.fields.forEach((field: SearchField) => {
            if (
                field.fieldValue.value ||
                field.operator.value === 'hasvalue' ||
                field.operator.value === 'hasnovalue'
            ) {
                filteredParams.push({
                    name: field.fieldValue.name,
                    operator: field.operator.value,
                    value: field.fieldValue.value
                });
            }
        });
        return filteredParams;
    }

    /**
     * Transform data from server as needed. Currently only turns unix timestamps into JS timestamps.
     *
     * @param searchData
     * @returns {*}
     */
    private processResults(searchData) {
        const processedResult = {};
        const searchResults = searchData.data;
        for (const result of searchResults) {
            Object.getOwnPropertyNames(result).forEach((field) => {
                const resultField = searchData.resultFields.find((f) => f.name === field);
                if (resultField && resultField.type === 'date') {
                    const ts = result[field];
                    // js timestamps are in ms, unix timestamps are in seconds
                    result[field] = new Date(ts * 1000);
                }
            });
        }
        return searchData;
    }
}
